import { Container, Inject } from 'typescript-ioc';
import { IReactIntlProvider } from '../../../infrastructure/react-intl-provider';
import { IntlShape } from 'react-intl';
import { IGlobalStorage } from '../../../infrastructure/global-storage';
import { IAccountDetailsDTO, IAccountsDAO } from '../../../api/accounts.dao';
import { IRouter } from '../../../infrastructure/router';
import { RouteParams, Routes } from '../../../routes-table';
import { action, observable } from 'mobx';
import { RADIX_PARAM } from '../../../constants/common.constants';
import { alertServerError } from '../../global/modal/alerts-host/alert-templates';
import { errorText } from '../../../constants/common-labels.constants';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../infrastructure/sub-windows';
import { EDIT_HUB_TEXT, NEW_HUB_TEXT } from '../event-hubs.text';
import { EventHubForm } from '../_event-hub-form/event-hub-form';
import { IEventHubDTO, IEventHubListDTO, IEventHubsDAO } from '../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../api/implementations/event-hubs.dao';

export class EventHubsListVM {
    @observable
    private accountDetails: IAccountDetailsDTO = null;
    @observable
    private eventHubsList: IEventHubDTO[] = [];
    @observable
    private eventHubIdToEditPopupMap: { [key: number]: ISubWindowFacade } = {};

    @observable
    public loadingAccountDetails: boolean = false;
    @observable
    public loadingEventHubs: boolean = false;

    constructor(
        @Inject private readonly intlProvider: IReactIntlProvider = Container.get(IReactIntlProvider),
        @Inject private readonly globalStorage: IGlobalStorage = Container.get(IGlobalStorage),
        @Inject private readonly accountsDAO: IAccountsDAO = Container.get(IAccountsDAO),
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly routingService: IRouter<RouteParams> = Container.get(IRouter),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
        @Inject private readonly subWindowsService: ISubWindowsService = Container.get(ISubWindowsService),
    ) {}

    public get intl(): IntlShape { return this.intlProvider.getIntl(); }

    public async initialize(): Promise<void> {
        await this.initAccountData();
        await this.loadEventHubs();
    }

    public getAccountName(): string {
        return this.accountDetails?.name ?? '';
    }

    public getEventHubsList(): IEventHubDTO[] {
        return this.eventHubsList;
    }

    public openCreateHubForm = (): void => {
        this.subWindowsService.showSubWindow({
            title: this.intl.formatMessage({ ...NEW_HUB_TEXT }, { prefix: '' }),
            componentToHost: EventHubForm,
            componentToHostProps: {
                accountData: this.getHubFormAccountData(),
                saveCompletedCallback: this.loadEventHubs
            }
        });
    };

    @action
    public openEditHubForm = (eventHubId: number): void => {
        if (this.isEditHubSubwindowShown(eventHubId)) {
            this.eventHubIdToEditPopupMap[eventHubId].maximize();
            return;
        }

        this.eventHubIdToEditPopupMap[eventHubId] = this.subWindowsService.showSubWindow({
            title: this.intl.formatMessage({ ...EDIT_HUB_TEXT }),
            componentToHost: EventHubForm,
            componentToHostProps: {
                accountData: this.getHubFormAccountData(),
                saveCompletedCallback: this.loadEventHubs,
                deleteCompletedCallback: async () => {
                    this.closeSubwindowById(eventHubId);
                    await this.loadEventHubs();
                },
                eventHubId: eventHubId
            }
        });
    };

    public getAccountId(): number {
        return parseInt(this.routeParams.accountId, RADIX_PARAM);
    }

    public navigateToHubConfiguration(eventHubId: number): void {
        const params = { accountId: this.getAccountId().toString(), eventHubId: eventHubId.toString() };
        this.routingService.navigate(Routes.EventHubAttendees, params);
    }

    public isEditHubSubwindowShown(eventHubId: number): boolean {
        return (
            this.eventHubIdToEditPopupMap[eventHubId] &&
            this.eventHubIdToEditPopupMap[eventHubId].getStatus() !== SubWindowStatus.Closed
        );
    }

    @action.bound
    private async loadEventHubs(): Promise<void> {
        try {
            this.startLoadingEventHubs();
            const response = await this.eventHubsDAO.getEventHubs(this.getAccountId());
            this.setEventHubsList(response);
        } catch (e) {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError(errorText(), errorMessage);
        } finally {
            this.stopLoadingEventHubs();
        }
    }

    @action
    private async initAccountData(): Promise<void> {
        let accountData = this.globalStorage.getAccount();

        if (accountData) {
            this.setAccountDetails(accountData);
        } else {
            await this.loadCurrentAccountData();
        }
    }

    @action
    private async loadCurrentAccountData(): Promise<void> {
        try {
            this.startLoadingAccountDetails();
            const response = await this.accountsDAO.readAccount(this.getAccountId());
            this.setAccountDetails(response);
        } catch (e) {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError(errorText(), errorMessage);
        } finally {
            this.stopLoadingAccountDetails();
        }
    }

    private get routeParams(): { accountId: string } {
        return this.routingService.router.getState().params;
    }

    private getHubFormAccountData(): { accountId: number; accountName: string } {
        return {
            accountId: this.getAccountId(),
            accountName: this.getAccountName()
        };
    }

    private closeSubwindowById = (eventHubId: number): void => {
        this.eventHubIdToEditPopupMap[eventHubId]?.close();
    };

    @action
    private setAccountDetails(accountDetails: IAccountDetailsDTO): void {
        this.accountDetails = accountDetails;
    }

    @action
    private startLoadingAccountDetails(): void {
        this.loadingAccountDetails = true;
    }
    @action
    private stopLoadingAccountDetails(): void {
        this.loadingAccountDetails = false;
    }

    @action
    private setEventHubsList(eventHubs: IEventHubListDTO): void {
        this.eventHubsList = eventHubs.content;
    }

    @action
    private startLoadingEventHubs(): void {
        this.loadingEventHubs = true;
    }
    @action
    private stopLoadingEventHubs(): void {
        this.loadingEventHubs = false;
    }

}